/**
 * Synergy Multimedia Academy (SMA) - Course Catalog & Syllabus PDF/PNG Export Utility
 * High-performance, lazy-loaded export generation for:
 * 1. Full Academic Programs & Curriculum Directory Table (PDF + PNG)
 * 2. Individual Program Factsheet & Syllabus (PDF + PNG)
 *
 * PDF output is drawn with jsPDF. PNG output builds an offscreen DOM node with
 * the same branded layout, captures it via the shared html2canvas helper, and
 * saves the canvas — so the PNG visually matches the PDF.
 */

import { captureElementToCanvas, saveCanvasAsPNG } from './pdfExport'

let jsPDFConstructor = null
let cachedLogoDataUrl = null

// Aligned with the shared document tokens (documentTokens.js) — same palette
// as the factsheet, receipts, and invoices. RGB arrays for jsPDF direct drawing.
const DIRECTORY_PALETTE = {
  warmWhite: [255, 255, 255], // PAPER (pure white)
  white: [255, 255, 255],
  charcoal: [28, 28, 28], // INK #1c1c1c
  navy: [28, 28, 28], // alias of INK
  gold: [163, 93, 44], // ACCENT #a35d2c
  grey: [111, 111, 111], // MUTE #6f6f6f
  warmGrey: [255, 255, 255], // no shaded backgrounds
  rowTint: [255, 255, 255], // no striped rows — hairline dividers only
  divider: [226, 224, 219], // RULE #e2e0db
}

/**
 * Dynamically load and cache SMA Logo as Data URL
 */
async function loadLogoData() {
  if (cachedLogoDataUrl) return cachedLogoDataUrl
  if (typeof window === 'undefined' || typeof document === 'undefined') return null

  try {
    const logoUrl = '/Logos/SMA-Logo-Dark.png'
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.src = logoUrl
    await new Promise((resolve) => {
      if (img.complete && img.naturalWidth) return resolve()
      img.onload = resolve
      img.onerror = () => resolve()
      setTimeout(resolve, 1500)
    })

    if (img.naturalWidth && img.naturalHeight) {
      // Downscale to a small thumbnail (max 200px) before converting to data URL.
      // Drawing the full 1773×1773 image to canvas produces a ~13 MB PNG data URL;
      // at 200px the result is a few KB — plenty for a 38mm PDF header logo.
      const maxDim = 200
      const scale = Math.min(1, maxDim / Math.max(img.naturalWidth, img.naturalHeight))
      const w = Math.round(img.naturalWidth * scale)
      const h = Math.round(img.naturalHeight * scale)

      const canvas = document.createElement('canvas')
      canvas.width = w
      canvas.height = h
      const ctx = canvas.getContext('2d')
      ctx.drawImage(img, 0, 0, w, h)
      cachedLogoDataUrl = {
        dataUrl: canvas.toDataURL('image/png'),
        aspectRatio: img.naturalWidth / img.naturalHeight,
      }
      return cachedLogoDataUrl
    }
  } catch {
    // Continue with text branding fallback
  }
  return null
}

/**
 * Dynamically import jsPDF to minimize initial bundle size
 */
async function loadJsPDF() {
  if (!jsPDFConstructor) {
    const mod = await import('jspdf')
    jsPDFConstructor = mod.jsPDF || mod.default
  }
  return jsPDFConstructor
}

/**
 * The old dark-banner header (drawBrandedHeader/drawBrandedFooter) was dead
 * code that diverged from the shared token system. All documents now use
 * drawDirectoryHeader/drawDirectoryFooter which inherit from
 * DIRECTORY_PALETTE (aligned with documentTokens.js).
 */

function drawDirectoryHeader(doc, titleText, generatedText, logoData) {
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()

  // Fill page background — no border, no frame
  doc.setFillColor(...DIRECTORY_PALETTE.warmWhite)
  doc.rect(0, 0, pageWidth, pageHeight, 'F')

  let textStartX = 14
  if (logoData?.dataUrl) {
    try {
      const logoH = 10
      const logoW = Math.min(12, Math.max(8, logoH * logoData.aspectRatio))
      doc.addImage(logoData.dataUrl, 'PNG', 14, 6.5, logoW, logoH)
      textStartX = 14 + logoW + 4
    } catch {
      textStartX = 14
    }
  }

  // IMPORTANT: Set text color BEFORE every text() call. jsPDF's addImage
  // can reset the text color state, causing text to render in the default
  // color (which appears blue in some PDF viewers). This was the root cause
  // of the "blue header text on page 2" bug — page 2 calls addImage for the
  // logo, then draws contact text without re-setting the color.

  // Brand name (left)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(10.5)
  doc.setTextColor(...DIRECTORY_PALETTE.navy)
  doc.text('Synergy Multimedia Academy', textStartX, 11.5)

  // Tagline (left)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(6.5)
  doc.setTextColor(...DIRECTORY_PALETTE.grey)
  doc.text('Shaping futures — Kampala, Uganda', textStartX, 16.5)

  // Contact info (right) — explicitly set color again after font change
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(6.3)
  doc.setTextColor(...DIRECTORY_PALETTE.grey)
  doc.text('Makerere I · Kuburi, Kampala', pageWidth - 14, 7.5, { align: 'right' })
  doc.text('+256 706 739 311 / +256 763 875 564', pageWidth - 14, 11.8, { align: 'right' })
  doc.text('synergymultimediaacademy@gmail.com', pageWidth - 14, 16.1, { align: 'right' })
  doc.text('www.synergymultimediaacademy.com', pageWidth - 14, 20.4, { align: 'right' })

  // Hairline divider under header
  doc.setDrawColor(...DIRECTORY_PALETTE.divider)
  doc.setLineWidth(0.2)
  doc.line(14, 24, pageWidth - 14, 24)

  // Document title — serif (Georgia in DOM → times in jsPDF)
  doc.setFont('times', 'normal')
  doc.setFontSize(15)
  doc.setTextColor(...DIRECTORY_PALETTE.navy)
  doc.text(titleText, 14, 33.5)

  // Subtitle + generated date
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(7)
  doc.setTextColor(...DIRECTORY_PALETTE.grey)
  doc.text('Programs directory', 14, 39)
  doc.text(generatedText, pageWidth - 14, 38.5, { align: 'right' })
}

function drawDirectoryFooter(doc, pageNum, totalPages) {
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()

  doc.setDrawColor(...DIRECTORY_PALETTE.divider)
  doc.setLineWidth(0.25)
  doc.line(14, pageHeight - 17, pageWidth - 14, pageHeight - 17)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(6.6)
  doc.setTextColor(...DIRECTORY_PALETTE.grey)
  doc.text(
    'Synergy Multimedia Academy  •  Official Academic Curriculum  •  Apply online at www.synergymultimediaacademy.com/register',
    14,
    pageHeight - 10.5
  )
  doc.text(`Page ${pageNum} of ${totalPages}`, pageWidth - 14, pageHeight - 10.5, { align: 'right' })
}

/**
 * 1. Export Full Programs Table Matrix to PDF (Multi-page support)
 * @param {Array} coursesList - Array of course objects (static or API-normalized)
 * @param {Object} options - Metadata options (category, level, search, programTypeLabel)
 */
export async function exportCoursesTablePDF(coursesList = [], options = {}) {
  const [jsPDF, logoData] = await Promise.all([loadJsPDF(), loadLogoData()])
  // Landscape A4 for wide table layout
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4',
  })

  const pageWidth = doc.internal.pageSize.getWidth() // 297mm
  const pageHeight = doc.internal.pageSize.getHeight() // 210mm
  const marginX = 14
  const startY = 48
  let currentY = startY

  // Table Column Specifications (Widths sum ~ 269mm)
  const columns = [
    { header: '#', width: 8, align: 'center' },
    { header: 'Code', width: 26, align: 'left' },
    { header: 'Program title', width: 62, align: 'left' },
    { header: 'Category', width: 34, align: 'left' },
    { header: 'Level', width: 24, align: 'center' },
    { header: 'Duration', width: 24, align: 'center' },
    { header: 'Tuition fee', width: 30, align: 'right' },
    { header: 'Career pathways', width: 61, align: 'left' },
  ]

  const programTypeLabel = options.programTypeLabel || 'Academic Programs'
  const directoryTitle = programTypeLabel === 'Corporate' ? 'Corporate Programs' : programTypeLabel
  const generatedText = `Generated ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`

  drawDirectoryHeader(doc, directoryTitle, generatedText, logoData)

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(7.2)
  doc.setTextColor(...DIRECTORY_PALETTE.grey)
  const filterDesc = [
    `${coursesList.length} program${coursesList.length === 1 ? '' : 's'}`,
    options.filterCategory && options.filterCategory !== 'All' ? `Category: ${options.filterCategory}` : null,
    options.filterLevel && options.filterLevel !== 'All' ? `Level: ${options.filterLevel}` : null,
  ].filter(Boolean).join('  •  ')
  doc.text(filterDesc, marginX, 44)

  function renderTableHeader(y) {
    // No shaded background — hairline divider only
    doc.setDrawColor(...DIRECTORY_PALETTE.divider)
    doc.setLineWidth(0.2)
    doc.line(marginX, y + 7.5, pageWidth - marginX, y + 7.5)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(7)
    doc.setTextColor(...DIRECTORY_PALETTE.grey)

    let curX = marginX
    columns.forEach((col) => {
      let textX = curX + 2
      if (col.align === 'center') textX = curX + (col.width / 2)
      if (col.align === 'right') textX = curX + col.width - 2
      doc.text(col.header, textX, y + 4.9, { align: col.align })
      curX += col.width
    })
    return y + 7.5
  }

  function drawCompactLines(text, x, y, width, fontSize, color, font = 'normal') {
    doc.setFont('helvetica', font)
    doc.setFontSize(fontSize)
    doc.setTextColor(...color)
    const lines = doc.splitTextToSize(text || '—', width).slice(0, 2)
    const textY = lines.length > 1 ? y + 3.25 : y + 5.15
    doc.text(lines, x, textY, { lineHeightFactor: 0.92 })
  }

  function drawLevel(level, x, y, width) {
    const value = level || 'Beginner'
    // Plain text — no pill badge, no border, no fill
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(6.8)
    doc.setTextColor(...DIRECTORY_PALETTE.grey)
    doc.text(value, x + width / 2, y + 5.15, { align: 'center' })
  }

  currentY = renderTableHeader(currentY)

  coursesList.forEach((course, index) => {
    const rowHeight = 8.2

    // Check if this row would overflow the available space. The footer
    // needs ~12mm, so break when we're within 14mm of the bottom. This
    // fills each page as much as possible before breaking.
    if (currentY + rowHeight > pageHeight - 14) {
      doc.addPage('a4', 'landscape')
      drawDirectoryHeader(doc, directoryTitle, generatedText, logoData)
      currentY = renderTableHeader(44)
    }

    let curX = marginX

    doc.setFont('helvetica', 'normal')
    doc.setFontSize(6.8)
    doc.setTextColor(...DIRECTORY_PALETTE.grey)
    doc.text(String(index + 1), curX + columns[0].width / 2, currentY + 5.15, { align: 'center' })
    curX += columns[0].width

    doc.setFont('courier', 'bold')
    doc.setFontSize(6.5)
    doc.setTextColor(...DIRECTORY_PALETTE.grey)
    doc.text(course.code || '—', curX + 2, currentY + 5.15)
    curX += columns[1].width

    drawCompactLines(course.title || 'Untitled', curX + 2, currentY, columns[2].width - 4, 7.6, DIRECTORY_PALETTE.navy, 'bold')
    curX += columns[2].width

    drawCompactLines(course.category || '—', curX + 2, currentY, columns[3].width - 4, 6.7, DIRECTORY_PALETTE.grey)
    curX += columns[3].width

    drawLevel(course.level, curX, currentY, columns[4].width)
    curX += columns[4].width

    doc.setFont('helvetica', 'normal')
    doc.setFontSize(6.8)
    doc.setTextColor(...DIRECTORY_PALETTE.charcoal)
    doc.text(course.duration || '3 months', curX + columns[5].width / 2, currentY + 5.15, { align: 'center' })
    curX += columns[5].width

    doc.setFont('helvetica', 'bold')
    doc.setFontSize(7.2)
    doc.setTextColor(...DIRECTORY_PALETTE.gold)
    // Format fee as "800,000 UGX" (number first) — matches reference
    const rawFee = course.fee || '—'
    const formattedFee = (typeof rawFee === 'string' && rawFee.includes('UGX'))
      ? rawFee.replace('UGX ', '').replace(' UGX', '') + ' UGX'
      : rawFee
    doc.text(formattedFee, curX + columns[6].width - 2, currentY + 5.15, { align: 'right' })
    curX += columns[6].width

    const career = course.jobOpportunities?.[0] || '—'
    drawCompactLines(career, curX + 2, currentY, columns[7].width - 4, 6.5, DIRECTORY_PALETTE.grey)

    currentY += rowHeight

    // Draw hairline divider AFTER the row content, but only if there are
    // more rows to come. This prevents a divider at the very bottom of a
    // page (which would appear as a black bar at the page boundary).
    if (index < coursesList.length - 1) {
      doc.setDrawColor(...DIRECTORY_PALETTE.divider)
      doc.setLineWidth(0.15)
      doc.line(marginX, currentY, pageWidth - marginX, currentY)
    }
  })

  const totalPages = doc.internal.getNumberOfPages()
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i)
    drawDirectoryFooter(doc, i, totalPages)
  }

  // Save the file
  const fileSlug = options.programTypeLabel
    ? options.programTypeLabel.replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '')
    : 'Academic-Programs-Directory'
  const fileName = `SMA-${fileSlug}-${new Date().getFullYear()}.pdf`
  doc.save(fileName)
  return fileName
}

/**
 * 2. Export Individual Course Factsheet & Syllabus to PDF
 *
 * Uses the same clean, table-based layout as the PNG factsheet and the
 * programs directory — a simple branded header, a two-column fact table,
 * an enroll callout, and a footer. No dense card grids or numbered sections.
 *
 * @param {Object} course - Detailed course object
 */
/**
 * Export Individual Course Factsheet & Syllabus to PDF.
 * Uses the same DOM-based rendering as the PNG export (buildFactsheetDOM),
 * captured via html2canvas and placed into a portrait A4 PDF — so the PDF
 * and PNG are pixel-identical and both inherit the shared design tokens.
 */
export async function exportSingleCoursePDF(course, options = {}) {
  if (!course) return
  const jsPDF = await loadJsPDF()
  const node = buildFactsheetDOM(course, options)

  // Attach offscreen for html2canvas capture
  node.style.position = 'fixed'
  node.style.left = '-99999px'
  node.style.top = '0'
  document.body.appendChild(node)

  try {
    const canvas = await captureElementToCanvas(node, {
      scale: 3,
      backgroundColor: '#ffffff',
    })

    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
    const pw = doc.internal.pageSize.getWidth()
    const ph = doc.internal.pageSize.getHeight()

    // Fit width-first, then constrain by height
    let pdfW = pw
    let pdfH = (canvas.height * pdfW) / canvas.width
    if (pdfH > ph) {
      pdfH = ph
      pdfW = (canvas.width * pdfH) / canvas.height
    }

    const pdfX = (pw - pdfW) / 2
    const pdfY = (ph - pdfH) / 2

    doc.addImage(
      canvas.toDataURL('image/png', 1.0),
      'PNG', pdfX, pdfY, pdfW, pdfH, '', 'FAST',
    )

    const cleanCode = (course.code || 'COURSE').replace(/[^a-zA-Z0-9-_]/g, '')
    const fileName = `SMA-Curriculum-${cleanCode}.pdf`
    doc.save(fileName)
    return fileName
  } finally {
    document.body.removeChild(node)
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  PNG exports — build an offscreen DOM node, capture with html2canvas, save  */
/* ────────────────────────────────────────────────────────────────────────── */

// Aligned with the shared document tokens (documentTokens.js) so all
// documents — receipts, invoices, curriculum sheets — share one palette.
const PNG_PALETTE = {
  warmWhite: '#ffffff', // PAPER (pure white, matches reference)
  white: '#ffffff',
  charcoal: '#1c1c1c', // INK — primary text
  navy: '#1c1c1c', // alias of INK (kept for compatibility with existing code)
  gold: '#a35d2c', // ACCENT — muted terracotta, used sparingly
  accentSoft: '#f6efe8', // ACCENT_SOFT — light terracotta wash for enroll block
  grey: '#6f6f6f', // MUTE — labels, contact info, footers
  warmGrey: '#ffffff', // no shaded backgrounds — use white
  rowTint: '#ffffff', // no striped rows — hairline dividers only
  divider: '#e2e0db', // RULE — hairline color
}

/**
 * Build an offscreen DOM node for the programs directory table, matching the
 * PDF layout. Returns the node (not yet attached to the document).
 */
function buildDirectoryDOM(coursesList, options = {}) {
  const programTypeLabel = options.programTypeLabel || 'Academic Programs'
  const directoryTitle = programTypeLabel === 'Corporate' ? 'Corporate Programs' : programTypeLabel
  const generatedText = `Generated ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`

  const filterDesc = [
    `${coursesList.length} program${coursesList.length === 1 ? '' : 's'}`,
    options.filterCategory && options.filterCategory !== 'All' ? `Category: ${options.filterCategory}` : null,
    options.filterLevel && options.filterLevel !== 'All' ? `Level: ${options.filterLevel}` : null,
  ].filter(Boolean).join('  •  ')

  // Format fee as "800,000 UGX" (number first, then UGX) — matches reference
  const formatFee = (fee) => {
    if (!fee || fee === '—') return '—'
    // If it's already a formatted string, return as-is
    if (typeof fee === 'string' && fee.includes('UGX')) {
      return fee.replace('UGX ', '').replace(' UGX', '') + ' UGX'
    }
    return fee
  }

  const wrapper = document.createElement('div')
  wrapper.style.cssText = `
    width: 1040px; background: ${PNG_PALETTE.white}; box-sizing: border-box;
    padding: 40px 48px 32px; font-family: 'Helvetica Neue', Arial, sans-serif;
    color: ${PNG_PALETTE.charcoal};
  `

  // ── Masthead — brand left, contact right ──────────────────────────────
  const masthead = document.createElement('div')
  masthead.style.cssText = `
    display: flex; justify-content: space-between; align-items: flex-start;
    padding-bottom: 18px;
  `
  masthead.innerHTML = `
    <div style="display: flex; align-items: center; gap: 12px;">
      <img src="/Logos/SMA LOGO  VERSION 2-04.png" alt="Synergy Multimedia Academy"
        style="height: 48px; width: auto; display: block; flex-shrink: 0;" />
      <div>
        <div style="font-size: 14px; font-weight: 700; color: ${PNG_PALETTE.charcoal};">Synergy Multimedia Academy</div>
        <div style="font-size: 10px; color: ${PNG_PALETTE.grey}; margin-top: 2px;">Shaping futures — Kampala, Uganda</div>
      </div>
    </div>
    <div style="text-align: right; font-size: 10px; color: ${PNG_PALETTE.grey}; line-height: 1.6;">
      Makerere I · Kuburi, Kampala<br>
      +256 706 739 311 / +256 763 875 564<br>
      synergymultimediaacademy@gmail.com<br>
      www.synergymultimediaacademy.com
    </div>
  `
  wrapper.appendChild(masthead)

  // ── Title row — ink border-top, serif title, meta right ────────────────
  const titleRow = document.createElement('div')
  titleRow.style.cssText = `
    border-top: 1px solid ${PNG_PALETTE.charcoal}; padding-top: 18px;
    display: flex; justify-content: space-between; align-items: baseline;
  `
  titleRow.innerHTML = `
    <div>
      <h1 style="font-family: Georgia, 'Times New Roman', serif; font-size: 22px; font-weight: 400; margin: 0; color: ${PNG_PALETTE.charcoal}; line-height: 1.2;">${directoryTitle}</h1>
      <div style="font-size: 11px; color: ${PNG_PALETTE.grey}; margin-top: 4px;">Programs directory · ${filterDesc}</div>
    </div>
    <div style="font-size: 10.5px; color: ${PNG_PALETTE.grey}; white-space: nowrap;">${generatedText}</div>
  `
  wrapper.appendChild(titleRow)

  // ── Table — ink border-bottom on header, hairline on rows ─────────────
  const table = document.createElement('table')
  table.style.cssText = `width: 100%; border-collapse: collapse; margin-top: 22px;`

  const thead = document.createElement('thead')
  const headRow = document.createElement('tr')
  const headers = ['#', 'Code', 'Program title', 'Category', 'Level', 'Duration', 'Tuition fee', 'Career pathways']
  headers.forEach((h, i) => {
    const th = document.createElement('th')
    const isNum = i === 6 // Tuition fee
    th.style.cssText = `
      text-align: ${isNum ? 'right' : 'left'}; font-size: 10px; color: ${PNG_PALETTE.grey};
      font-weight: 400; padding-bottom: 8px; border-bottom: 1px solid ${PNG_PALETTE.charcoal};
    `
    th.textContent = h
    headRow.appendChild(th)
  })
  thead.appendChild(headRow)
  table.appendChild(thead)

  const tbody = document.createElement('tbody')
  coursesList.forEach((course, index) => {
    const tr = document.createElement('tr')
    tr.style.borderBottom = `1px solid ${PNG_PALETTE.divider}`

    const cells = [
      { text: String(index + 1), cls: 'idx' },
      { text: course.code || '—', cls: 'code' },
      { text: course.title || 'Untitled', cls: 'title' },
      { text: course.category || '—', cls: '' },
      { text: course.level || 'Beginner', cls: 'level', isLevel: true },
      { text: course.duration || '3 months', cls: '' },
      { text: formatFee(course.fee), cls: 'fee', isNum: true },
      { text: course.jobOpportunities?.[0] || '—', cls: '' },
    ]

    cells.forEach((cell) => {
      const td = document.createElement('td')
      td.style.cssText = `padding: 11px 10px 11px 0; font-size: 12px; vertical-align: top; line-height: 1.4; color: ${PNG_PALETTE.charcoal};`

      if (cell.cls === 'idx') {
        td.style.color = PNG_PALETTE.grey
      } else if (cell.cls === 'code') {
        td.style.color = PNG_PALETTE.grey
        td.style.fontFamily = 'Menlo, Consolas, monospace'
        td.style.fontSize = '10.5px'
      } else if (cell.cls === 'title') {
        td.style.fontWeight = '600'
      } else if (cell.cls === 'fee') {
        td.style.color = PNG_PALETTE.gold
        td.style.fontWeight = '700'
        td.style.textAlign = 'right'
        td.style.whiteSpace = 'nowrap'
      }

      // Level cell — plain text, no pill badge
      if (cell.isLevel) {
        td.textContent = cell.text
      } else {
        td.textContent = cell.text
      }
      tr.appendChild(td)
    })
    tbody.appendChild(tr)
  })
  table.appendChild(tbody)
  wrapper.appendChild(table)

  // ── Footer — hairline top, space-between, muted ───────────────────────
  const footer = document.createElement('div')
  footer.style.cssText = `
    margin-top: 24px; padding-top: 12px; border-top: 1px solid ${PNG_PALETTE.divider};
    font-size: 9.5px; color: ${PNG_PALETTE.grey};
    display: flex; justify-content: space-between;
  `
  footer.innerHTML = `
    <span>Synergy Multimedia Academy · Official academic curriculum · Apply online at synergymultimediaacademy.com/register</span>
    <span>Page 1 of 1</span>
  `
  wrapper.appendChild(footer)

  return wrapper
}

/**
 * Build an offscreen DOM node for a single course factsheet, matching the
 * curriculum-redesign.html reference mockup: generous padding, masthead with
 * brand left + contact right, ink border-top title block with serif title,
 * hairline-divider field rows (no shaded backgrounds), accent-soft enroll
 * block with left accent border, and a space-between footer.
 *
 * Returns the node (not yet attached to the document).
 */
function buildFactsheetDOM(course, options = {}) {
  const programTypeLabel = options.programTypeLabel || 'Regular programs'
  const subLabel = `${programTypeLabel.toLowerCase()} — Curriculum & syllabus`

  const wrapper = document.createElement('div')
  wrapper.style.cssText = `
    width: 800px; background: ${PNG_PALETTE.white}; box-sizing: border-box;
    padding: 64px 56px 48px; font-family: 'Helvetica Neue', Arial, sans-serif;
    color: ${PNG_PALETTE.charcoal};
  `

  // ── Masthead — brand left, contact right ──────────────────────────────
  const masthead = document.createElement('div')
  masthead.style.cssText = `
    display: flex; justify-content: space-between; align-items: flex-start;
    padding-bottom: 28px;
  `
  masthead.innerHTML = `
    <div style="display: flex; align-items: center; gap: 12px;">
      <img src="/Logos/SMA LOGO  VERSION 2-04.png" alt="Synergy Multimedia Academy"
        style="height: 102px; width: auto; display: block; flex-shrink: 0;" />
      <div>
        <div style="font-size: 16px; font-weight: 700; color: ${PNG_PALETTE.charcoal}; letter-spacing: 0.01em;">
          Synergy Multimedia Academy
        </div>
        <div style="font-size: 11px; color: ${PNG_PALETTE.grey}; margin-top: 2px; letter-spacing: 0.02em;">
          Shaping futures — Kampala, Uganda
        </div>
      </div>
    </div>
    <div style="text-align: right; font-size: 11px; color: ${PNG_PALETTE.grey}; line-height: 1.7;">
      Makerere I · Kuburi, Kampala<br>
      +256 706 739 311 / +256 763 875 564<br>
      synergymultimediaacademy@gmail.com<br>
      www.synergymultimediaacademy.com
    </div>
  `
  wrapper.appendChild(masthead)

  // ── Title block — ink border-top, serif title, sentence-case sub ──────
  const titleBlock = document.createElement('div')
  titleBlock.style.cssText = `
    border-top: 1px solid ${PNG_PALETTE.charcoal}; padding-top: 24px;
    display: flex; justify-content: space-between; align-items: baseline;
  `
  titleBlock.innerHTML = `
    <div>
      <h1 style="
        font-family: Georgia, 'Times New Roman', serif; font-size: 30px;
        font-weight: 400; margin: 0; line-height: 1.2; color: ${PNG_PALETTE.charcoal};
      ">${course.title || 'Untitled'}</h1>
      <div style="font-size: 12px; color: ${PNG_PALETTE.grey}; margin-top: 6px;">
        ${subLabel}
      </div>
    </div>
    <div style="font-size: 11px; color: ${PNG_PALETTE.grey}; white-space: nowrap; padding-left: 24px;">
      ${course.code || 'SMA'}
    </div>
  `
  wrapper.appendChild(titleBlock)

  // ── Field rows — hairline dividers, no shaded backgrounds ─────────────
  const rows = [
    ['Category', course.category || 'Multimedia'],
    ['Level', course.level || 'Beginner'],
    ['Duration', course.duration || '3 months'],
    ['Tuition fee', course.fee || 'Contact Admissions'],
    ['Learning format', '100% studio practical'],
  ]
  if (course.description) rows.push(['Overview', course.description])
  if (course.advantages?.length) rows.push(['Key highlights', course.advantages.slice(0, 4).join('  •  ')])
  if (course.whatYouCanDo?.length) rows.push(['Portfolio deliverables', course.whatYouCanDo.slice(0, 3).join('  •  ')])
  if (course.jobOpportunities?.length) rows.push(['Career pathways', course.jobOpportunities.join('  •  ')])

  const fields = document.createElement('div')
  fields.style.cssText = `margin-top: 36px;`

  rows.forEach(([label, value], index) => {
    const fieldRow = document.createElement('div')
    const isFirst = index === 0
    fieldRow.style.cssText = `
      display: flex; padding: 16px 0; gap: 32px;
      border-bottom: 1px solid ${PNG_PALETTE.divider};
      ${isFirst ? `border-top: 1px solid ${PNG_PALETTE.divider};` : ''}
    `

    const labelDiv = document.createElement('div')
    labelDiv.style.cssText = `
      width: 170px; flex-shrink: 0; font-size: 12px; color: ${PNG_PALETTE.grey};
      padding-top: 1px;
    `
    labelDiv.textContent = label

    const valueDiv = document.createElement('div')
    const isFee = label === 'Tuition fee'
    valueDiv.style.cssText = `
      font-size: ${isFee ? '15px' : '14px'}; line-height: 1.55;
      ${isFee ? `color: ${PNG_PALETTE.gold}; font-weight: 600;` : `color: ${PNG_PALETTE.charcoal};`}
    `
    valueDiv.textContent = value

    fieldRow.appendChild(labelDiv)
    fieldRow.appendChild(valueDiv)
    fields.appendChild(fieldRow)
  })
  wrapper.appendChild(fields)

  // ── Enroll block — accent-soft background, left accent border ────────
  const enroll = document.createElement('div')
  enroll.style.cssText = `
    margin-top: 36px; padding: 22px 24px;
    background: ${PNG_PALETTE.accentSoft}; border-left: 2px solid ${PNG_PALETTE.gold};
  `
  enroll.innerHTML = `
    <div style="font-size: 11px; color: ${PNG_PALETTE.gold}; font-weight: 600; margin-bottom: 6px;">
      How to enroll
    </div>
    <div style="font-size: 13px; line-height: 1.6; color: ${PNG_PALETTE.charcoal};">
      Visit <span style="font-weight: 600;">synergymultimediaacademy.com/register?course=${course.code || ''}</span>
      or call +256 706 739 311 / +256 763 875 564.
      50% deposit before start, balance due during study.
    </div>
  `
  wrapper.appendChild(enroll)

  // ── Footer — hairline top, space-between, muted ───────────────────────
  const footer = document.createElement('div')
  footer.style.cssText = `
    margin-top: 40px; padding-top: 16px;
    border-top: 1px solid ${PNG_PALETTE.divider};
    font-size: 10.5px; color: ${PNG_PALETTE.grey};
    display: flex; justify-content: space-between;
  `
  footer.innerHTML = `
    <span>Synergy Multimedia Academy — Official academic curriculum</span>
    <span>Apply online at synergymultimediaacademy.com/register</span>
  `
  wrapper.appendChild(footer)

  return wrapper
}

/**
 * Capture an offscreen DOM node to PNG. Attaches the node offscreen, captures
 * it with the shared html2canvas helper, saves the PNG, then removes the node.
 */
async function captureDOMToPNG(node, filename) {
  // Attach offscreen so html2canvas can measure/render it
  node.style.position = 'fixed'
  node.style.left = '-99999px'
  node.style.top = '0'
  document.body.appendChild(node)
  try {
    const canvas = await captureElementToCanvas(node, { scale: 2, backgroundColor: '#ffffff' })
    saveCanvasAsPNG(canvas, filename)
    return filename
  } finally {
    document.body.removeChild(node)
  }
}

/**
 * Export Full Programs Table Matrix to PNG.
 * @param {Array} coursesList - Array of course objects
 * @param {Object} options - Metadata options (category, level, search, programTypeLabel)
 */
export async function exportCoursesTablePNG(coursesList = [], options = {}) {
  const node = buildDirectoryDOM(coursesList, options)
  const fileSlug = options.programTypeLabel
    ? options.programTypeLabel.replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '')
    : 'Academic-Programs-Directory'
  const fileName = `SMA-${fileSlug}-${new Date().getFullYear()}.png`
  return captureDOMToPNG(node, fileName)
}

/**
 * Export Individual Course Factsheet & Syllabus to PNG.
 * @param {Object} course - Detailed course object
 * @param {Object} options
 */
export async function exportSingleCoursePNG(course, options = {}) {
  if (!course) return
  const node = buildFactsheetDOM(course, options)
  const cleanCode = (course.code || 'COURSE').replace(/[^a-zA-Z0-9-_]/g, '')
  const fileName = `SMA-Curriculum-${cleanCode}.png`
  return captureDOMToPNG(node, fileName)
}
